import React from "react";
import { Card, Ring } from "./TvBits";

const M = (v) => { const a = Math.abs(v || 0); return a >= 1e6 ? `$${(a / 1e6).toFixed(1)}M` : `$${Math.round(a / 1e3)}k`; };
export const KIND = {
  directional: { label: "DIRECTIONAL", color: "#10F5A0" },
  hedge: { label: "HEDGE", color: "#F5C451" },
  "two-sided": { label: "TWO-SIDED", color: "#7DD3FC" },
  mixed: { label: "MIXED", color: "#94A3B8" },
  none: { label: "NO FLOW", color: "#475569" },
  error: { label: "NO DATA", color: "#475569" },
};
export const kindOf = (r) => KIND[r.kind] || KIND.none;
export const dirColor = (r) => (r.direction === "short" ? "#FF3B6B" : "#10F5A0");

export const FlowBar = ({ r, h = "h-2.5" }) => {
  const t = r.total || 1;
  return (
    <div className={`flex ${h} rounded-full overflow-hidden bg-white/5`}>
      <div style={{ width: `${(r.bull / t) * 100}%`, background: "#10F5A0", boxShadow: "0 0 8px #10F5A0" }} />
      <div style={{ width: `${(r.hedge / t) * 100}%`, background: "#F5C451" }} />
      <div style={{ width: `${(r.bear / t) * 100}%`, background: "#FF3B6B", boxShadow: "0 0 8px #FF3B6B" }} />
    </div>
  );
};

const Tile = ({ r }) => {
  const k = kindOf(r), q = r.qualifies;
  return (
    <div className={`nx-hud rounded-2xl p-3 flex flex-col gap-1.5 relative overflow-hidden ${q ? "ring-2" : ""}`} style={q ? { "--tw-ring-color": dirColor(r), boxShadow: `0 0 28px ${dirColor(r)}55` } : {}}
      data-testid={`tv-earn-${r.ticker}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="nx-orbitron font-black text-cyan-50 text-[clamp(1.1rem,1.6vw,1.8rem)] leading-none">{r.ticker}</p>
          <p className="nx-mono text-[10px] text-cyan-200/50 mt-1 truncate">{r.report} · {r.window}</p>
        </div>
        <Ring value={r.confidence || 0} color={q ? dirColor(r) : k.color} size={58} label={`${Math.round(r.confidence || 0)}`} sub="%" />
      </div>
      <div className="flex items-center gap-2">
        <span className="px-2 py-0.5 rounded-full nx-mono text-[10px] tracking-widest font-bold border" style={{ color: k.color, borderColor: `${k.color}66`, background: `${k.color}14` }}>{k.label}</span>
        {r.kind === "directional" && <span className="nx-mono text-xs font-black" style={{ color: dirColor(r) }}>{r.direction === "short" ? "▼ SHORT" : "▲ LONG"}</span>}
        {q && <span className="ml-auto nx-mono text-[10px] font-black tracking-widest text-[#030712] px-2 py-0.5 rounded" style={{ background: dirColor(r) }}>QUALIFIES</span>}
      </div>
      {r.total > 0 && <FlowBar r={r} />}
      <div className="flex justify-between nx-mono text-[11px] text-cyan-100/60">
        <span style={{ color: "#10F5A0" }}>▲{M(r.bull)}</span><span style={{ color: "#F5C451" }}>H {M(r.hedge)}</span><span style={{ color: "#FF3B6B" }}>▼{M(r.bear)}</span>
      </div>
      <div className="flex justify-between nx-mono text-[11px] text-cyan-200/50">
        <span>±{r.expected_move_pct}% move</span><span>{r.alerts} alerts</span><span>${r.price?.toFixed?.(2)}</span>
      </div>
      {q && <p className="nx-mono text-[11px] text-cyan-50">E {r.entry} · T <span style={{ color: "#10F5A0" }}>{r.target}</span> · S <span style={{ color: "#FF3B6B" }}>{r.stop}</span></p>}
    </div>
  );
};

export const TvEarningsFlow = ({ d }) => {
  const e = d.earnings_flow;
  if (!e) return null;
  const b = (e.board || []).filter((r) => r.kind !== "error");
  const count = (k) => b.filter((r) => r.kind === k).length;
  const q = b.filter((r) => r.qualifies);
  return (
    <div className="h-full flex flex-col gap-4" data-testid="tv-scene-earnings">
      <div className="grid grid-cols-6 gap-4">
        {[["NAMES SCANNED", b.length, "#00F0FF"], ["QUALIFIED ≥" + e.min + "%", q.length, "#10F5A0"], ["DIRECTIONAL", count("directional"), "#10F5A0"],
          ["HEDGE", count("hedge"), "#F5C451"], ["TWO-SIDED", count("two-sided") + count("mixed"), "#7DD3FC"],
          ["LAST SCAN", e.at ? new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—", "#94A3B8"]].map(([k, v, c]) => (
          <div key={k} className="nx-hud rounded-2xl px-4 py-2" data-testid={`tv-earn-stat-${k.split(" ")[0].toLowerCase()}`}>
            <p className="nx-mono text-[10px] tracking-[0.3em] text-cyan-200/50">{k}</p>
            <p className="nx-orbitron font-black text-[clamp(1.4rem,2vw,2.3rem)]" style={{ color: c, textShadow: `0 0 16px ${c}55` }}>{v}</p>
          </div>
        ))}
      </div>
      <Card title="EARNINGS × UNUSUAL FLOW" accent="#10F5A0" code="HEDGE vs DIRECTIONAL · ▲BULL H·HEDGE ▼BEAR" className="flex-1">
        <div className="grid grid-cols-5 gap-3 h-full content-start">{b.slice(0, 15).map((r) => <Tile key={r.ticker} r={r} />)}</div>
      </Card>
    </div>
  );
};
